import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { BodyPart, IDamageLocation, IPoint, DamageType, ICustomDamageType } from 'redux/damages/damages';
import { ConfirmCancelModal, ConfirmCancelModalPropTypes } from 'components/partials';
import { BodyPartModal, DamageTypeModal } from './modal.component';
import { ImageTypeKeys } from 'redux/workflows/workflows';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { useIsScreenPortrait, useTheme } from 'hooks';
import {
  addNewDamage,
  deleteNewDamage
} from 'redux/root.actions';
import { CapturedImageWithZoom, ZoomArea } from '../captured-image-with-zoom.component';
import { getCustomDamageTypes } from '../../../../../../../redux/damages/damages.selectors';
import { calculateDamageLocationMask } from 'redux/damages/damages.reducer';
import { useTranslate } from 'services';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/root.selectors';
import { lightThemeBackground, darkThemeBackground, useIsDarkTheme } from 'hooks/useTheme/useTheme.util';
import { InstructionWrapper, InstructionWrapperCol1, InstructionWrapperCol2 } from '../../../instructions/instructions.component';
import { WindshieldDamageMarking } from 'assets/components/instructions';
import { DeleteX, SvgWrapper } from 'assets';
import { ReactComponent as Bulb } from 'assets/svgs/icn-bulb2.svg';
import { ReactComponent as Check } from 'assets/svgs/icn-check.svg';
import { track_event } from 'utils';

const { damage_labelling, damage_labelling_crack_repair } = TRANSLATIONS_VALUES_KEYS;

/* For future windshield overlay implementation */
const RENDER_WINDSHIELD_OVERLAY = false;

const Svg = styled.svg<{ height: number, width: number }>`
  position: absolute;
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
`;

// SmartScan base font size is fixed to 16px
const pxPerRem = 16;
const lineWidthRems = 1;
const lineWidthBufferPx = lineWidthRems * pxPerRem;

const windshieldBoundingBoxLineWidthRems = 0.1;

const Path = styled.path<{ lineWidth: number }>`
  fill: none;
  stroke-width: ${(props) => `${props.lineWidth}rem`};
  stroke-linejoin: round;
  stroke-linecap: round;
  opacity: 0.7;
`;

const Circle = styled.circle`
  opacity: 0.7;
`;

const CaptureImageWrapper = styled.div`
  z-index: 2;
  touch-action: none;
`;

const Header = styled.h1<{ color: string, background: string }>`
  font-style: normal;
  font-weight: bold;
  font-size: 0.75rem;
  line-height: 200%;
  color: ${({ color }) => color};
  background: ${({ background }) => background};
  border-radius: 0rem 0rem 0.875rem 0.875rem;
  padding: 0.375rem 1.5625rem;
  width: fit-content;
  height: fit-content;
  margin: 0 auto;
  position: absolute;
`;

export type Line = {
  lineId: number;
  coords: IPoint[];
  zoom: boolean;
  lineWidthMultiplier?: number;
};

export type ExitZoomMode = {
  undoDamages: boolean;
}

type DamageLabellingAreaProps = {
  damages: Line[];
  setDamages: (damages: Line[]) => void;

  disabled?: boolean;

  imageWidth: number;
  imageHeight: number;
  imageId: string,
  imageType: number;
  imageSubType: number;
  imageObjectUrl: string;

  exitZoomMode?: ExitZoomMode;
  showFullPageInstructions: boolean;
  showInstructions: boolean;

  detectedWindshield?: IPoint[] | null | undefined;

  onDrawStart?: () => void;
  onDamageTypeSelected?: () => void;
  onZoomAreaSelected?: (selected: boolean) => void;

  noDamagesWarning: NoDamagesWarning | null;
}

export type NoDamagesWarning = {
  blockContinue: boolean,
  onContinue: () => void,
  onCancel?: () => void
}

type ImageSize = {
  height: number,
  width: number,
}

type ImageSizeOnScreen = {
  dimensions: ImageSize,
  oldDimensions?: ImageSize | undefined
}

export const DamageLabellingArea = ({
  damages, setDamages, imageId, imageWidth, imageHeight, imageType, imageSubType, imageObjectUrl, exitZoomMode,
  onDrawStart, onDamageTypeSelected, onZoomAreaSelected, showInstructions, noDamagesWarning,
  showFullPageInstructions, detectedWindshield,
  disabled = false
}: DamageLabellingAreaProps) => {
  /* Refs */
  const imageRef = useRef<HTMLImageElement>(null!);

  /* Functional component states */
  const [isDrawing, setIsDrawing] = useState(false);
  const [imageSizeOnScreen, setImageSizeOnScreen] = useState<ImageSizeOnScreen>({
    dimensions: { height: 0, width: 0 }
  });
  const [showBodyPartModal, setShowBodyPartModal] = useState(false);
  const [showDamageTypeModal, setShowDamageTypeModal] = useState(false);
  const [selectedBodyPart, setSelectedBodyPart] = useState<BodyPart | undefined>(undefined);
  const [confirmDeleteLine, setConfirmDeleteLine] = useState<number | null>(null);

  const [outsideWindshieldPrompt, setOutsideWindshieldPrompt] = useState<null | {
    lineId: number;
    autoSelectWindshield: boolean;
  }>(null);

  const [zoomArea, setZoomArea] = useState<ZoomArea | null>(null);

  /* Redux */
  const dispatch = useDispatch();

  /* Selectors */
  const selectCustomDamageTypes : ICustomDamageType[] = useSelector(getCustomDamageTypes);
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);

  const damageLabellingTranslationCategory = photoSeries.manualDamageLabellingCrackRepairEnabled
    ? damage_labelling_crack_repair
    : damage_labelling;

  const isWindshieldImage = imageType === ImageTypeKeys.Windshield;

  /* Hooks */
  const isPortrait = useIsScreenPortrait();
  const { translate } = useTranslate();

  const getMaxCoords = useCallback((coords: IPoint[], buffer: number) => {
    const xCoords = coords.map((coord) => coord.x);
    const yCoords = coords.map((coord) => coord.y);

    const maxX = Math.max(...xCoords) + buffer;
    const maxY = Math.max(...yCoords) + buffer;

    return {
      x: maxX > imageSizeOnScreen.dimensions.width ? imageSizeOnScreen.dimensions.width : maxX,
      y: maxY > imageSizeOnScreen.dimensions.height ? imageSizeOnScreen.dimensions.height : maxY,
    };
  }, [imageSizeOnScreen]);

  const getMinCoords = useCallback((coords: IPoint[], buffer: number): IPoint => {
    const xCoords = coords.map((coord) => coord.x);
    const yCoords = coords.map((coord) => coord.y);

    const calculatedMinX = Math.min(...xCoords) - buffer;
    const calculatedMinY = Math.min(...yCoords) - buffer;

    return {
      x: calculatedMinX < 0 ? 0 : calculatedMinX,
      y: calculatedMinY < 0 ? 0 : calculatedMinY,
    };
  }, []);

  const getLastDamageBoundingBox = (buffer: number): IDamageLocation => {
    const lastLine = damages[damages.length - 1];
    return getDamageBoundingBoxFromCoords(lastLine.coords, buffer);
  };
  const getDamageBoundingBoxFromCoords = useCallback((coords: IPoint[], buffer: number): IDamageLocation => ({
    mask: [
      getMinCoords(coords, buffer),
      getMaxCoords(coords, buffer)
    ]
  }), [getMinCoords, getMaxCoords]);

  const getLastDamageOutline = (buffer: number): IDamageLocation => {
    const lastLine = damages[damages.length - 1];
    return getStrokeOutline(lastLine.coords, buffer);
  };

  // Utility function to convert touch points to buffered path outline polygon
  const sortOutlinePoints = useCallback((outline: IPoint[]): IPoint[] => {
    const key = (p: IPoint) => `${p.x},${p.y}`;
    const pointSet = new Set(outline.map(key));
    const visited = new Set<string>();

    const directions = [
      { dx: -1, dy: -1 }, { dx: 0, dy: -1 }, { dx: 1, dy: -1 },
      { dx: -1, dy: 0 }, { dx: 1, dy: 0 },
      { dx: -1, dy: 1 }, { dx: 0, dy: 1 }, { dx: 1, dy: 1 },
    ];

    const path: IPoint[] = [];

    // Start with any point
    let current = outline[0];
    path.push(current);
    visited.add(key(current));

    let hasNext = true;

    while (hasNext) {
      const { x: cx, y: cy } = current;

      const next = directions
        .map(({ dx, dy }) => ({ x: cx + dx, y: cy + dy }))
        .find((p) => {
          const k = key(p);
          return pointSet.has(k) && !visited.has(k);
        });

      if (next) {
        current = next;
        path.push(current);
        visited.add(key(current));
      } else {
        hasNext = false;
      }
    }

    return path;
  }, []);

  const extractOutline = useCallback((
    ctx: CanvasRenderingContext2D,
    height: number = imageSizeOnScreen.dimensions.height,
    width: number = imageSizeOnScreen.dimensions.width,
  ) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const isFilled = (x: number, y: number) => {
      const i = (y * width + x) * 4;
      return data[i + 3] > 0; // alpha > 0
    };

    const outline: { x: number; y: number; }[] = [];
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let isCenterFilled = isFilled(x, y);
        let hasEmptyNeighbor = false;

        if (isCenterFilled) {
          for (let dy = -1; dy <= 1 && !hasEmptyNeighbor; dy++) {
            for (let dx = -1; dx <= 1 && !hasEmptyNeighbor; dx++) {
              let isCenter = dx === 0 && dy === 0;
              let neighborFilled = isFilled(x + dx, y + dy);

              if (!isCenter && !neighborFilled) {
                hasEmptyNeighbor = true;
              }
            }
          }
        }

        if (isCenterFilled && hasEmptyNeighbor) {
          outline.push({ x, y });
        }
      }
    }

    const sortedMask = sortOutlinePoints(outline);

    return sortedMask;
  }, [imageSizeOnScreen, sortOutlinePoints]);

  const getStrokeOutline = useCallback((touchPoints: IPoint[], diameter: number): IDamageLocation => {
    const canvas = document.createElement('canvas');
    canvas.width = imageSizeOnScreen.dimensions.width;
    canvas.height = imageSizeOnScreen.dimensions.height;
    const ctx = canvas.getContext('2d')!;

    // Set internal resolution
    canvas.width = imageSizeOnScreen.dimensions.width;
    canvas.height = imageSizeOnScreen.dimensions.height;

    // Draw the stroke
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = diameter;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'black';

    ctx.beginPath();
    ctx.moveTo(touchPoints[0].x, touchPoints[0].y);
    for (let i = 1; i < touchPoints.length; i++) {
      ctx.lineTo(touchPoints[i].x, touchPoints[i].y);
    }
    ctx.stroke();

    const mask = extractOutline(ctx);

    return { mask };
  }, [imageSizeOnScreen, extractOutline]);

  const resetDimensions = () => {
    // Store the old dimensions before the resize happens
    setImageSizeOnScreen((prev) => ({
      dimensions: prev.dimensions,
      oldDimensions: { height: prev.dimensions.height, width: prev.dimensions.width }
    }));
  };

  /* Effects */
  useEffect(() => {
    // window.addEventListener('touchcancel', handleTouchEnd);
    window.addEventListener('resize', resetDimensions);

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', resetDimensions);
    } else {
      window.addEventListener('orientationchange', resetDimensions);
    }

    return () => {
      // window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('resize', resetDimensions);

      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', resetDimensions);
      } else {
        window.removeEventListener('orientationchange', resetDimensions);
      }
    };
  }, []); // Empty dependency array to register listeners only once

  useLayoutEffect(() => {
    if (imageSizeOnScreen.oldDimensions !== undefined && imageRef.current) {
      const height = imageRef.current.height;
      const width = imageRef.current.width;

      // Additional safety check to ensure dimensions are valid
      if (!height || !width) {
        return;
      }

      const oldHeight = imageSizeOnScreen.oldDimensions.height;
      const oldWidth = imageSizeOnScreen.oldDimensions.width;

      if (damages.length > 0) {
        const heightRatio = height / oldHeight;
        const widthRatio = width / oldWidth;

        setDamages(damages.map((line) => ({
          lineId: line.lineId,
          coords: line.coords.map((point) => ({
            x: point.x * widthRatio, // Fixed: use widthRatio for x coordinates
            y: point.y * heightRatio // Fixed: use heightRatio for y coordinates
          })),
          zoom: line.zoom,
          lineWidthMultiplier: line.lineWidthMultiplier
        })));
      }

      setImageSizeOnScreen({
        dimensions: { height, width },
        oldDimensions: undefined
      });
    }
  }, [imageSizeOnScreen, imageRef, damages, setDamages]);

  useEffect(() => {
    if (!exitZoomMode) return;
    if (isDrawing) return;
    if (onZoomAreaSelected) {
      onZoomAreaSelected(false);
    }

    if (zoomArea && zoomArea.cropped) {
      const newLines = exitZoomMode.undoDamages
        ? damages.filter((line) => !line.zoom)
        : damages.map((line) => {
          if (line.zoom) {
            const croppedImageWidthRatio = (zoomArea.cropped?.width! / imageSizeOnScreen.dimensions.width);
            const croppedImageHeightRatio = (zoomArea.cropped?.height! / imageSizeOnScreen.dimensions.height);

            const downscaleWidthRatio = imageSizeOnScreen.dimensions.width / imageWidth;
            const downscaleHeightRatio = imageSizeOnScreen.dimensions.height / imageHeight;

            const mask = line.coords.map((point) => (
              {
                x: (zoomArea.cropped?.x! + (point.x * croppedImageWidthRatio)) * downscaleWidthRatio,
                y: (zoomArea.cropped?.y! + (point.y * croppedImageHeightRatio)) * downscaleHeightRatio
              }
            ));

            // When user taps a single point in zoomed mode, we count it as a chip
            const isChip = mask.length === 1;

            const damage = {
              bodyPart: BodyPart.Windshield,
              damageType: isChip ? DamageType.Chip : DamageType.Crack,
              damageLocation: { mask: [{ x: 0, y: 0 }] },
              customDamageTypeId: undefined,
              imageType,
              lineId: line.lineId,
              isSmall: null as boolean | null
            };

            if (photoSeries.manualDamageLabellingCrackRepairEnabled && !isChip) {
              damage.isSmall = true;
            }

            const imageData = { width: imageWidth, height: imageHeight };
            const screenFeedData = { width: imageSizeOnScreen.dimensions.width, height: imageSizeOnScreen.dimensions.height };

            // Set narrow line width for zoomed area damages
            const lineWidthMultiplier = 0.5;

            if (isChip) {
              damage.damageLocation = getDamageBoundingBoxFromCoords(mask, lineWidthBufferPx * lineWidthMultiplier);
            } else {
              damage.damageLocation = getStrokeOutline(mask, lineWidthBufferPx * lineWidthMultiplier);
            }

            dispatch(addNewDamage(imageId, damage, imageData, screenFeedData));

            return {
              lineId: line.lineId,
              coords: mask,
              zoom: false,
              lineWidthMultiplier,
            };
          }

          return line;
        });

      setDamages(newLines);
      setZoomArea(null);
    }
  }, [
    exitZoomMode, damages, onZoomAreaSelected, imageSizeOnScreen, imageHeight, imageWidth, zoomArea,
    dispatch, imageId, imageType, setDamages, isDrawing,
    getDamageBoundingBoxFromCoords, getStrokeOutline,
    photoSeries.manualDamageLabellingCrackRepairEnabled
  ]);

  /* Helpers */
  const cancelDrawing = useCallback(() => {
    if (isDrawing) {
      // Remove the incomplete line that was being drawn
      const copyOfLines = [...damages];
      copyOfLines.pop(); // Remove the last (incomplete) line
      setDamages(copyOfLines);
      setIsDrawing(false);
    }
  }, [damages, isDrawing, setDamages]);

  useEffect(() => {
    if (!disabled) return;

    cancelDrawing();
    setConfirmDeleteLine(null);
  }, [cancelDrawing, disabled]);

  const getCoordinatesForTouch = (touch: React.Touch): IPoint => {
    if (!imageRef.current) {
      // Return a safe default coordinate if imageRef is null
      return { x: 0, y: 0 };
    }

    const imagePosition = imageRef.current.getBoundingClientRect();
    if (isPortrait) {
      return {
        x: touch.clientY - imageRef.current.offsetLeft,
        y: imagePosition.width - touch.clientX
      };
    }

    return {
      x: touch.clientX - imageRef.current.offsetLeft,
      y: touch.clientY - imagePosition.top
    };
  };

  /* Event handlers */
  const handleBodyPartSelected = (selectedBodyPart: BodyPart) => {
    if (photoSeries.manualLabellingWindshieldDamageDetection && isWindshieldImage) {
      // We save all damages by default as cracks
      handleDamageTypeSelected(DamageType.Crack, undefined, selectedBodyPart);
      return;
    }

    setSelectedBodyPart(selectedBodyPart);
    setShowBodyPartModal(false);

    if (zoomArea !== null) {
      handleDamageTypeSelected(DamageType.Chip, undefined, selectedBodyPart);
    } else {
      setShowDamageTypeModal(true);
    }
  };

  const handleDamageTypeSelected = (selectedDamageType: DamageType, customDamageTypeId?: string, bodyPart?: BodyPart) => {
    const currentBodyPart = bodyPart || selectedBodyPart;
    if (!currentBodyPart) {
      setShowBodyPartModal(true);
      return;
    }

    const imageData = { width: imageWidth, height: imageHeight };
    const screenFeedData = { width: imageSizeOnScreen.dimensions.width, height: imageSizeOnScreen.dimensions.height };
    const lastDamage = damages[damages.length - 1];
    const zoomAreaFlow = (selectedDamageType === DamageType.Chip || photoSeries.manualLabellingWindshieldDamageDetection) && isWindshieldImage;

    if (zoomAreaFlow && zoomArea !== null) {
      // no-op, we convert back after user has finished zooming
    } else if (zoomAreaFlow && zoomArea == null) {
      const damageLocationMask = calculateDamageLocationMask(getLastDamageBoundingBox(lineWidthBufferPx), imageData, screenFeedData);
      setZoomArea({
        requested: {
          x: damageLocationMask[0].x,
          y: damageLocationMask[0].y,
          width: damageLocationMask[1].x - damageLocationMask[0].x,
          height: damageLocationMask[1].y - damageLocationMask[0].y
        }
      });

      // remove line from lines as it is only used for zooming into chip damage
      const deletedLine = lastDamage;
      setDamages(damages.filter((line) => line.lineId !== deletedLine.lineId));
      setShowDamageTypeModal(false);

      if (onZoomAreaSelected) onZoomAreaSelected(true);
    } else {
      const damage = {
        bodyPart: currentBodyPart,
        damageType: selectedDamageType,
        damageLocation: getLastDamageOutline(lineWidthBufferPx * (lastDamage.lineWidthMultiplier || 1)),
        customDamageTypeId,
        imageType,
        lineId: lastDamage.lineId,
      };

      setShowDamageTypeModal(false);
      setSelectedBodyPart(undefined);
      dispatch(addNewDamage(imageId, damage, imageData, screenFeedData));

      if (onDamageTypeSelected) onDamageTypeSelected();
    }
  };

  const handlePathClick = (lineId: number) => {
    if (disabled) return;
    setConfirmDeleteLine(lineId);
  };

  const handleConfirmDelete = () => {
    const removedLine = damages.find((line) => line.lineId === confirmDeleteLine);
    if (!removedLine) {
      setConfirmDeleteLine(null);
      return;
    }

    if (!removedLine.zoom) {
      // Damages with "zoom = false" are also stored in Redux, we need to remove them
      dispatch(deleteNewDamage(imageId, removedLine.lineId));
    }

    setDamages([
      ...damages.filter((line: Line) => (line.lineId !== confirmDeleteLine)),
    ]);

    setConfirmDeleteLine(null);
  };

  /* Touch event handlers */
  const handleTouchStart = (touchEvent: React.TouchEvent<SVGSVGElement>) => {
    if (disabled) return;

    // Safety check for imageRef
    if (!imageRef.current) {
      return;
    }

    // Ignore multi-touch (e.g., pinch-to-zoom gestures)
    if (touchEvent.touches.length > 1) {
      // Cancel any ongoing drawing if user adds another finger
      cancelDrawing();
      return;
    }
    if (confirmDeleteLine) return;
    if (outsideWindshieldPrompt) return;

    const touch = touchEvent.changedTouches[0];
    const point = getCoordinatesForTouch(touch);
    // Using damages.length + 1 would reuse an existing lineId once earlier damages are deleted,
    // causing color collisions and deleting multiple damages at once (lineId is filtered on, not indexed).
    const nextLineId = damages.reduce((maxId, line) => Math.max(maxId, line.lineId), 0) + 1;
    const newDamage = {
      lineId: nextLineId,
      coords: [point],
      zoom: zoomArea !== null,
    };

    setDamages([
      ...damages,
      newDamage
    ]);
    setIsDrawing(true);
    if (onDrawStart) onDrawStart();
  };

  const handleTouchMove = (touchEvent: React.TouchEvent<SVGSVGElement>) => {
    if (disabled) return;
    if (!isDrawing) return;

    // Safety check for imageRef
    if (!imageRef.current) {
      cancelDrawing();
      return;
    }

    // Ignore multi-touch - stop drawing if user adds another finger
    if (touchEvent.touches.length > 1) {
      cancelDrawing();
      return;
    }

    const allowDrag = (photoSeries.manualDamageLabellingCrackRepairEnabled ||
                       photoSeries.manualLabellingWindshieldDamageDetection) && isWindshieldImage;

    // Dragging is only enabled on zoomed-in area for marking repairable cracks
    if (!allowDrag && zoomArea !== null) return;

    const touch = touchEvent.changedTouches[0];
    const point = getCoordinatesForTouch(touch);
    const lastLineIndex = damages.length - 1;

    const updatedLines = [...damages];
    updatedLines[lastLineIndex] = {
      ...damages[lastLineIndex],
      coords: [
        ...damages[lastLineIndex].coords,
        point,
      ],
    };

    setDamages(updatedLines);
  };

  const handleTouchEnd = () => {
    if (disabled) return;
    if (!isDrawing) return;

    if (zoomArea === null) {
      const copyOfLines = [...damages];
      const firstLine = copyOfLines.pop();

      if (firstLine && firstLine.coords.length > 1) {
        const isDamageCompletelyOutsideWindshield = (() => {
          if (!detectedWindshield || detectedWindshield.length < 3) return false;
          const screenWidth = imageSizeOnScreen.dimensions.width;
          const screenHeight = imageSizeOnScreen.dimensions.height;
          if (!imageWidth || !imageHeight || !screenWidth || !screenHeight) return false;

          const scaleX = screenWidth / imageWidth;
          const scaleY = screenHeight / imageHeight;

          const polygon = detectedWindshield.map((p) => ({
            x: p.x * scaleX,
            y: p.y * scaleY,
          }));

          const isPointInPolygon = (px: number, py: number) => {
            let inside = false;
            for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
              const xi = polygon[i].x;
              const yi = polygon[i].y;
              const xj = polygon[j].x;
              const yj = polygon[j].y;
              if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
                inside = !inside;
              }
            }
            return inside;
          };

          return firstLine.coords.every((p) => !isPointInPolygon(p.x, p.y));
        })();

        if (isDamageCompletelyOutsideWindshield) {
          track_event('damage_outside_of_windshield_prompt_shown');
          setOutsideWindshieldPrompt({
            lineId: firstLine.lineId,
            autoSelectWindshield: isWindshieldImage
          });
          setIsDrawing(false);
          return;
        }

        if (isWindshieldImage) {
          handleBodyPartSelected(BodyPart.Windshield);
        } else {
          setShowBodyPartModal(true);
        }
      }
    // If we are in zoom we select damage type automatically
    } else if (onDamageTypeSelected) onDamageTypeSelected();

    setIsDrawing(false);
  };

  const handleOutsideWindshieldYes = () => {
    const prompt = outsideWindshieldPrompt;
    setOutsideWindshieldPrompt(null);
    if (!prompt) return;

    if (prompt.autoSelectWindshield) {
      handleBodyPartSelected(BodyPart.Windshield);
    } else {
      setShowBodyPartModal(true);
    }
  };

  const handleOutsideWindshieldNo = () => {
    const lineId = outsideWindshieldPrompt?.lineId;
    setOutsideWindshieldPrompt(null);
    if (!lineId) return;
    setDamages(damages.filter((line) => line.lineId !== lineId));
  };

  const onDamageTypeCancel = () => {
    if (disabled) return;
    const copyOfLines = [...damages];
    copyOfLines.pop();
    setDamages(copyOfLines);
    setShowDamageTypeModal(false);
  };

  const showModal = outsideWindshieldPrompt !== null || confirmDeleteLine || noDamagesWarning !== null;
  let modalSettings: ConfirmCancelModalPropTypes = {
    confirmTextTranslationKey: '',
    onConfirm: null,
    onCancel: null
  };

  if (outsideWindshieldPrompt) {
    modalSettings = {
      confirmTextTranslationKey: damage_labelling.damage_outside_of_windshield,
      onConfirm: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_yes,
        click: handleOutsideWindshieldYes
      },
      onCancel: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_no,
        click: handleOutsideWindshieldNo
      }
    };
  // Confirming line delete will take priority
  } else if (confirmDeleteLine) {
    modalSettings = {
      confirmTextTranslationKey: damage_labelling.confirm_damage_removal_question,
      onConfirm: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_yes,
        click: handleConfirmDelete
      },
      onCancel: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_no,
        click: () => setConfirmDeleteLine(null)
      }
    };
  } else if (noDamagesWarning !== null && !noDamagesWarning.blockContinue) {
    modalSettings = {
      confirmTextTranslationKey: damage_labelling.confirm_no_damages_question,
      onConfirm: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_yes,
        click: noDamagesWarning.onContinue
      },
      onCancel: {
        buttonTextTranslationKey: damage_labelling.confirm_damage_removal_no,
        click: noDamagesWarning.onCancel!
      }
    };
  } else if (noDamagesWarning && noDamagesWarning.blockContinue) {
    modalSettings = {
      confirmTextTranslationKey: damage_labelling.damage_marking_required_text,
      onConfirm: {
        buttonTextTranslationKey: damage_labelling.damage_marking_required_button_text,
        click: noDamagesWarning.onContinue
      },
      onCancel: null
    };
  }

  if (showFullPageInstructions) return <WindshieldInstructions />;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <CaptureImageWrapper>
        <CapturedImageWithZoom
          imageRef={imageRef}
          src={imageObjectUrl}
          alt={imageId}
          onLoad={(event: any) => {
            const target = event.target;
            if (!target || !target.height || !target.width) {
              return;
            }

            const { height, width } = target;
            setImageSizeOnScreen({
              dimensions: { height, width }
            });
          }}
          zoomArea={zoomArea}
          setZoomArea={setZoomArea}
        >
          <Drawing
            handleLineClick={handlePathClick}
            lines={zoomArea !== null ? damages.filter((line: Line) => line.zoom) : damages.filter((line: Line) => !line.zoom)}
            height={imageSizeOnScreen.dimensions.height}
            width={imageSizeOnScreen.dimensions.width}
            isDrawing={isDrawing}
            currentlyDrawingLineId={isDrawing && damages.length > 0 ? damages[damages.length - 1].lineId : null}
            showDeleteIcons={zoomArea === null}
            detectedWindshield={detectedWindshield}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            onTouchStart={handleTouchStart}
            onTouchCancel={handleTouchEnd}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            renderWindshieldOverlay={RENDER_WINDSHIELD_OVERLAY && zoomArea === null}
          />
          <>
            {
              showInstructions && (
                <Header background={lightThemeBackground.colorBackgroundBase} color={darkThemeBackground.colorBackgroundBase}>
                  {
                    zoomArea === null
                      ? translate(damage_labelling.drag_screen_instructions)
                      : translate(damageLabellingTranslationCategory.mark_chip_instructions)
                  }
                </Header>
              )
            }
          </>
        </CapturedImageWithZoom>
      </CaptureImageWrapper>
      { showBodyPartModal && (<BodyPartModal imageSubType={imageSubType} onBodyPartSelected={handleBodyPartSelected} />) }
      {
        showDamageTypeModal && (
          <DamageTypeModal
            customDamageTypes={selectCustomDamageTypes}
            onDamageTypeSelected={handleDamageTypeSelected}
            bodyPart={selectedBodyPart}
            onCancel={onDamageTypeCancel}
          />
        )
      }
      {
        showModal && <ConfirmCancelModal {...modalSettings} />
      }
    </div>
  );
};

const Row1 = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 3rem;
  width: 100%;
`;

const Title = styled.h1<{ color?: string }>`
  font-style: bold;
  font-size: 1.75rem;
  color: ${(props) => props.color || 'inherit'};
`;

const SubTitle = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  color: ${(props) => props.color || 'inherit'};
`;

const Row2 = styled.div`
  display: flex;
`;

const Column = styled.div<{ flex: number }>`
  flex: ${(props) => `${props.flex}%`};
`;

const Bubble = styled.div<{ background: string, paddingTop?: number }>`
  display: flex;
  align-items: center;
  padding: 0.6rem;
  background-color: ${(props) => props.background};
  border-radius: 0.5rem;
  margin-top: ${(props) => props.paddingTop ? `${props.paddingTop}rem` : '0'};
  margin-right: 2rem;
`;

const BubbleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  & > div {
    display: flex;
    align-items: center;
  }
`;

const BubbleText = styled.span<{ color: string }>`
  font-size: 0.75rem;
  margin-left: 0.5rem;
  font-weight: 600;
  color: ${(props) => props.color};
`;

const WindshieldInstructions = () => {
  const { translate } = useTranslate();
  const {
    styles: {
      colorParagraph: defaultColorParagraph,
      colorBackgroundGradient: defaultColorBackgroundGradient,
      colorBackgroundRaised: defaultColorBackgroundRaised,
      colorBackgroundBase: defaultColorBackgroundBase,
      colorPrimaryButtonBackground
    }
  } = useTheme();

  const isDarkTheme = useIsDarkTheme();

  const colors = {
    colorBackgroundGradient: defaultColorBackgroundGradient,
    colorBackgroundRaised: defaultColorBackgroundRaised,
    colorBackgroundBase: defaultColorBackgroundBase,
    colorParagraph: defaultColorParagraph
  };

  return (
    <InstructionWrapper className="wrapper">
      <InstructionWrapperCol1
        background={colors.colorBackgroundGradient}
      >
        <Row1>
          <Title color={colors.colorParagraph}>
            {translate(damage_labelling.instructions.title)}
          </Title>
          <SubTitle color={colors.colorParagraph}>
            {translate(damage_labelling.instructions.windshield)}
          </SubTitle>
        </Row1>
        <Row2>
          <Column flex={50}>
            <Bubble background={colors.colorBackgroundRaised}>
              <BubbleContent>
                <div>
                  <SvgWrapper
                    size="extra-small"
                    fill={colors.colorBackgroundRaised}
                    stroke={colors.colorParagraph}
                  >
                    <Check />
                  </SvgWrapper>
                  <BubbleText color={colors.colorParagraph}>
                    {translate(damage_labelling.instructions.tip_1)}
                  </BubbleText>
                </div>
                <div>
                  <SvgWrapper
                    size="extra-small"
                    fill={colors.colorBackgroundRaised}
                    stroke={colors.colorParagraph}
                  >
                    <Check />
                  </SvgWrapper>
                  <BubbleText color={colors.colorParagraph}>
                    {translate(damage_labelling.instructions.tip_2)}
                  </BubbleText>
                </div>
              </BubbleContent>
            </Bubble>
            <Bubble background={colors.colorBackgroundRaised} paddingTop={3}>
              <SvgWrapper
                size="small"
                style={{ width: '1.5rem' }}
                fill={colors.colorBackgroundRaised}
                stroke={colors.colorParagraph}
              >
                <Bulb />
              </SvgWrapper>
              <BubbleText color={colors.colorParagraph}>
                {translate(damage_labelling.instructions.hint)}
              </BubbleText>
            </Bubble>
          </Column>
          <Column flex={50}>
            <SvgWrapper size="large">
              <WindshieldDamageMarking
                animated
                damageColor={colorPrimaryButtonBackground}
                stroke={isDarkTheme ? 'white' : colors.colorParagraph}
              />
            </SvgWrapper>
          </Column>
        </Row2>
      </InstructionWrapperCol1>
      <InstructionWrapperCol2 background={colors.colorBackgroundBase} />
    </InstructionWrapper>
  );
};

type DrawingPropTypes = {
  lines: Line[];
  handleLineClick: Function;
  height: number;
  width: number;
  isDrawing: boolean;
  currentlyDrawingLineId: number | null;
  showDeleteIcons: boolean;
  detectedWindshield?: IPoint[] | null | undefined;
  imageWidth: number;
  imageHeight: number;
  onTouchStart: React.TouchEventHandler<SVGSVGElement>;
  onTouchMove: React.TouchEventHandler<SVGSVGElement>;
  onTouchEnd: React.TouchEventHandler<SVGSVGElement>;
  onTouchCancel: React.TouchEventHandler<SVGSVGElement>;
  renderWindshieldOverlay: boolean;
};

const Drawing: React.FC<DrawingPropTypes> = ({
  lines,
  handleLineClick,
  height,
  width,
  isDrawing,
  currentlyDrawingLineId,
  showDeleteIcons,
  detectedWindshield,
  imageWidth,
  imageHeight,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
  renderWindshieldOverlay
}) => (
  <Svg
    data-testid="drawing-area"
    className="drawing"
    height={height}
    width={width}
    onTouchStart={onTouchStart}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
    onTouchCancel={onTouchCancel}
  >
    {
      renderWindshieldOverlay && (
        <WindshieldBoundingBoxOverlay
          detectedWindshield={detectedWindshield}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          screenWidth={width}
          screenHeight={height}
        />
      )
    }
    {lines
      .map((line) =>
        (
          <DrawingLine
            key={`${line.lineId}${line.coords.map((p) => (`${p.x}${p.y}`)).join('')}`}
            line={line}
            onClick={handleLineClick}
            height={height}
            width={width}
            isDrawing={isDrawing}
            currentlyDrawingLineId={currentlyDrawingLineId}
            showDeleteIcons={showDeleteIcons}
          />
        ))}
  </Svg>
);

type DrawingLinePropTypes = {
  line: Line;
  onClick: Function;
  height: number;
  width: number;
  isDrawing: boolean;
  currentlyDrawingLineId: number | null;
  showDeleteIcons: boolean;
};

const getColor = (lineId: number) => {
  const colorIndex = lineId < 10
    ? lineId
    : Number(String(lineId).slice(-1));

  return colors[colorIndex];
};

const DrawingLine: React.FC<DrawingLinePropTypes> = ({
  line,
  onClick,
  height,
  width,
  isDrawing,
  currentlyDrawingLineId,
  showDeleteIcons,
}) => {
  const iconSizePx = 18;
  const iconGapPx = 1;

  const stopTouchPropagation = (event: any) => {
    event.stopPropagation();
  };

  const stopTouchPropagationAndPrevent = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleDeleteTouchStart = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    onClick(line.lineId);
  };

  const handleDeleteClick = (event: any) => {
    event.stopPropagation();
    onClick(line.lineId);
  };

  const getIconPosition = () => {
    const xs = line.coords.map((p) => p.x);
    const ys = line.coords.map((p) => p.y);

    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);

    const buffer = (lineWidthBufferPx * (line.lineWidthMultiplier || 1)) / 4;

    // Place icon slightly outside the damage bounding box (top-right), then clamp to viewport.
    let x = maxX + buffer + iconGapPx;
    let y = minY - buffer - iconSizePx - iconGapPx;

    x = Math.min(Math.max(x, 0), width - iconSizePx);
    y = Math.min(Math.max(y, 0), height - iconSizePx);

    return { x, y };
  };

  const renderDeleteIcon = () => {
    if (!showDeleteIcons) return null;
    if (isDrawing && currentlyDrawingLineId === line.lineId) return null;
    const { x, y } = getIconPosition();
    return (
      <DeleteX
        x={x}
        y={y}
        width={`${iconSizePx}px`}
        height={`${iconSizePx}px`}
        onTouchStart={handleDeleteTouchStart}
        onTouchEnd={stopTouchPropagationAndPrevent}
        onClick={handleDeleteClick}
      />
    );
  };

  if (line.coords.length > 1) {
    const pathData = `M ${line.coords.map((p) => (`${p.x} ${p.y}`)).join(' L ')}`;
    const lineWidth = line.lineWidthMultiplier ? lineWidthRems * line.lineWidthMultiplier : lineWidthRems;
    return (
      <g>
        <Path
          className="path"
          d={pathData}
          stroke={`rgba(${getColor(line.lineId)}, 0.7)`}
          onTouchStart={stopTouchPropagation}
          onTouchEnd={stopTouchPropagationAndPrevent}
          lineWidth={lineWidth}
        />
        {renderDeleteIcon()}
      </g>
    );
  }

  if (!line.zoom && !line.lineWidthMultiplier) return null;

  const diameter = line.lineWidthMultiplier !== undefined
    ? lineWidthBufferPx * line.lineWidthMultiplier
    : lineWidthBufferPx;

  return (
    <g>
      <Circle
        className="path"
        cx={line.coords[0].x}
        cy={line.coords[0].y}
        r={diameter / 2}
        fill={`rgba(${getColor(line.lineId)}, 0.7)`}
        onTouchStart={stopTouchPropagation}
        onTouchEnd={stopTouchPropagationAndPrevent}
      />
      {renderDeleteIcon()}
    </g>
  );
};

const WindshieldBoundingBoxStrokePath = styled.path<{ lineWidth: number }>`
  fill: none;
  stroke: black;
  stroke-width: ${(props) => `${props.lineWidth}rem`};
  stroke-linejoin: round;
  stroke-linecap: round;
  pointer-events: none;
`;

const WindshieldBoundingBoxOutsideOverlayPath = styled.path`
  fill: rgba(0, 0, 0, 0.70);
  pointer-events: none;
`;

type WindshieldBoundingBoxOverlayProps = {
  detectedWindshield?: IPoint[] | null | undefined;
  imageWidth: number;
  imageHeight: number;
  screenWidth: number;
  screenHeight: number;
};

const WindshieldBoundingBoxOverlay: React.FC<WindshieldBoundingBoxOverlayProps> = ({
  detectedWindshield,
  imageWidth,
  imageHeight,
  screenWidth,
  screenHeight,
}) => {
  if (!detectedWindshield || detectedWindshield.length < 3) return null;
  if (!imageWidth || !imageHeight) return null;
  if (!screenWidth || !screenHeight) return null;

  const scaleX = screenWidth / imageWidth;
  const scaleY = screenHeight / imageHeight;

  const scaledPoints = detectedWindshield.map((p) => ({
    x: Math.max(0, Math.min(p.x * scaleX, screenWidth)),
    y: Math.max(0, Math.min(p.y * scaleY, screenHeight)),
  }));

  const pointsPath = scaledPoints.map((p) => `${p.x} ${p.y}`).join(' L ');
  const d = `M ${pointsPath} Z`;
  const outsideOverlayD = `M 0 0 H ${screenWidth} V ${screenHeight} H 0 Z M ${pointsPath} Z`;

  return (
    <>
      <WindshieldBoundingBoxOutsideOverlayPath d={outsideOverlayD} fillRule="evenodd" />
      <WindshieldBoundingBoxStrokePath
        className="windshield-bounding-box"
        d={d}
        lineWidth={windshieldBoundingBoxLineWidthRems}
      />
    </>
  );
};

const colors = [
  '255,0,0',
  '0,255,0',
  '0,0,255',
  '255,255,0',
  '255,0,255',
  '210,105,30',
  '0,255,255',
  '138,43,226',
  '255,140,0',
  '143,188,143',
];
